'use client'
import styles from './ArticlesBlock.module.scss'
import PageGutter from '@/src/components/common/PageGutter'
import PageSectionTitle from '@/src/components/common/PageSectionTitle'
import { classNames } from '@/src/helpers/component'
import { Fragment, useEffect, useState } from 'react'
import { Block_Articles, Block_Articles_Article_Card_1, Maybe } from '@/src/generated/graphql'
import DirectusImage from '@/src/components/common/DirectusImage'
import { useSearchParams } from 'next/navigation'
import { stricmp, titleCase } from '@/src/helpers/string'
import { setSearchParamValue } from '@/src/helpers/url'
import { SEARCH_PARAM_FOR_CATEGORY } from '@/src/components/blocks/FeatureBlock/ArticlesBlock/index'
import WysiwygContent from '@/src/components/common/WysiwygContent'
import Link from 'next/link'

export default function ArticlesBlockClient(_: Block_Articles) {
  const searchParams = useSearchParams()
  const [search, setSearch] = useState(searchParams?.get('search') ?? '')
  const [filter, setFilter] = useState(searchParams?.get(SEARCH_PARAM_FOR_CATEGORY) ?? 'all')
  let categoriesFilter: string[] = []

  const handleSearchTextChanged = (_: WindowEventMap['search-text-change']) => {
    setSearch(_.detail)
  }

  const handleCategoryFilter = (category: string) => {
    history.replaceState(
      {},
      '',
      setSearchParamValue(SEARCH_PARAM_FOR_CATEGORY, titleCase(category)),
    )
    setFilter(category)
  }

  useEffect(() => {
    window.addEventListener('search-text-change', handleSearchTextChanged)
    return () => {
      window.removeEventListener('search-text-change', handleSearchTextChanged)
    }
  }, [])

  if (!_.cards) return null

  const searchKeywords = [
    ...new Set([...search.matchAll(/[a-z0-9]+/gi)].flat().map(_ => _.toLowerCase())),
  ]

  const filterSearch = (junction: Maybe<Block_Articles_Article_Card_1>) => {
    const card = junction?.article_card_id
    return (
      searchKeywords.filter(k =>
        `${card?.title ?? ''} ${card?.content ?? ''}`.match(new RegExp(`${k}`, 'i')),
      ).length === searchKeywords.length
    )
  }

  categoriesFilter = [
    ...new Set(
      _.cards
        .filter(filterSearch)
        .map(junction => junction?.article_card_id?.category?.toLowerCase()),
    ),
  ] as string[]
  if (categoriesFilter.length) categoriesFilter.unshift('All')

  const filteredArticleJunctions: Maybe<Block_Articles_Article_Card_1>[] | null = _.cards.filter(
    junction => {
      return (
        (stricmp(filter, junction?.article_card_id?.category) || stricmp(filter, 'All')) &&
        filterSearch(junction)
      )
    },
  )
  const filteredArticles = filteredArticleJunctions.map(j => j?.article_card_id)

  let featuredArticle = null

  if (_.featured_first_card) featuredArticle = filteredArticles.shift()

  return (
    <div className={styles.container}>
      <PageGutter className={styles.gutterContent}>
        <PageSectionTitle
          align={'left'}
          header={_.heading ?? ''}
          text={_.subheading ?? ''}
          className={styles.sectionTitle}
        />

        {categoriesFilter.length > 0 && _.show_category_filters && (
          <div className={styles.filter}>
            {categoriesFilter.map((category: string, idx: number) => (
              <div
                key={idx}
                onClick={() => handleCategoryFilter(category)}
                className={classNames(stricmp(filter, category) ? styles.active : '')}>
                {category}
              </div>
            ))}
          </div>
        )}

        {featuredArticle && (
          <>
            <div className={classNames(styles.featured, styles.card)}>
              <div className={styles.imageContainer}>
                <DirectusImage directusFile={featuredArticle.image} alt={''} loading={'lazy'} />
              </div>
              <div className={styles.contentContainer}>
                {featuredArticle.category && (
                  <div className={styles.label}>{featuredArticle.category}</div>
                )}
                <div>
                  <h3 className={styles.title}>{featuredArticle.title}</h3>
                  {featuredArticle.content && (
                    <WysiwygContent
                      className={styles.content}
                      content={featuredArticle.content ?? ''}
                    />
                  )}
                </div>
                <Link
                  className={styles.readmore}
                  href={
                    featuredArticle.linked_page?.slug
                      ? `/${featuredArticle.linked_page?.slug}/`
                      : '#'
                  }>
                  READ MORE
                </Link>
              </div>
            </div>

            {!!filteredArticles.length && <div className={styles.divider} />}
          </>
        )}

        <div className={styles.articleGroup}>
          {filteredArticles.map((card, idx) => (
            <Link href={card?.linked_page?.slug ? `/${card.linked_page.slug}/` : '#'} key={idx}>
              <div className={styles.card}>
                <div className={styles.imageContainer}>
                  <DirectusImage directusFile={card?.image} alt={''} loading={'lazy'} />
                </div>
                <div className={styles.contentContainer}>
                  {card?.category && <div className={styles.label}>{card.category}</div>}
                  <div>
                    {card?.title && <h5 className={styles.title}>{card.title}</h5>}
                    {card?.content && (
                      <WysiwygContent className={styles.content} content={card.content} />
                    )}
                  </div>
                </div>
              </div>
              {idx < filteredArticles.length - 1 && <div className={styles.divider} />}
            </Link>
          )) ?? (featuredArticle ? '' : 'No results')}
        </div>
      </PageGutter>
    </div>
  )
}
